import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { cn } from "@/lib/utils";
import { useState, useEffect, FC, useMemo } from "react";
import { useToneOfVoiceCrud } from "@/pages/settings/tone-of-voice/hooks/useToneOfVoiceCrud";
import ToneOfVoice from "@/models/tone-of-voice/toneOfVoice";
import { useConversationHub } from "@/hooks/useConversationHub";
import { getDomain, getDomainName } from "@/utils/domain";
import {
  getLockedToneChipLabel,
  getToneDetailsForDisplay,
} from "@/lib/conversationToneDisplay";
import { Check, ChevronDown, Info, Lock } from "lucide-react";

type ToneOfVoicePopoverProps = {
  disabled: boolean;
};

/** Half-height vertical rule, vertically centered in the toolbar row (avoids Radix Separator stretch overriding alignment). */
const ToneToolbarVerticalRule: FC = () => (
  <div
    className="flex shrink-0 self-stretch flex-col items-center justify-center px-0.5"
    aria-hidden
  >
    <span className="block h-[21px] w-px shrink-0 rounded-full bg-border" />
  </div>
);

const ToneOfVoicePopover: FC<ToneOfVoicePopoverProps> = ({ disabled }) => {
  const { selectedConversation, toneOfVoice, setToneOfVoice } =
    useConversationHub();
  const { fetchedToneOfVoice, isLoading } = useToneOfVoiceCrud();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const catalog = fetchedToneOfVoice.data?.data;

  const toneDetails = useMemo(
    () => getToneDetailsForDisplay(selectedConversation, catalog, toneOfVoice),
    [selectedConversation, catalog, toneOfVoice]
  );

  const canOpenDetails =
    Boolean(toneDetails.title) ||
    Boolean(toneDetails.description) ||
    Boolean(toneDetails.prompt);

  const chipLabel = useMemo(() => {
    if (!selectedConversation || selectedConversation.id === "temp-id") {
      return toneOfVoice?.title?.trim() ?? "";
    }
    return getLockedToneChipLabel(selectedConversation, catalog);
  }, [selectedConversation, toneOfVoice?.title, catalog]);

  const summaryTitle =
    chipLabel.trim() ||
    toneDetails.title.trim() ||
    toneOfVoice?.title?.trim() ||
    "";

  const summaryDescription = toneDetails.description.trim();

  const showSummaryCard = Boolean(summaryTitle) || Boolean(summaryDescription);

  const lockedPromptExcerpt = useMemo(() => {
    const raw = selectedConversation?.toneOfVoice?.trim();
    if (!raw || selectedConversation?.id === "temp-id") {
      return "";
    }
    return raw.length <= 220 ? raw : `${raw.slice(0, 217)}…`;
  }, [selectedConversation?.toneOfVoice, selectedConversation?.id]);

  const getCurrentToneId = () => {
    if (selectedConversation?.toneOfVoiceId) {
      return selectedConversation.toneOfVoiceId;
    }

    if (!selectedConversation || selectedConversation.id === "temp-id") {
      return toneOfVoice?.id ?? null;
    }

    const defaultTone = fetchedToneOfVoice.data?.data?.find(
      (tov) => tov.isDefault
    );
    return defaultTone?.id ?? null;
  };

  const currentToneId = getCurrentToneId();

  useEffect(() => {
    if (!isLoading && fetchedToneOfVoice.data?.data) {
      if (selectedConversation?.toneOfVoiceId) {
        const selectedTone = fetchedToneOfVoice.data.data.find(
          (tone) => tone.id === selectedConversation.toneOfVoiceId
        );
        if (selectedTone) {
          setToneOfVoice(selectedTone);
        }
      } else if (
        !selectedConversation ||
        selectedConversation.id === "temp-id"
      ) {
        const defaultTone = fetchedToneOfVoice.data.data.find(
          (tone) => tone.isDefault
        );
        if (defaultTone) {
          setToneOfVoice(defaultTone);
        }
      }
    }
  }, [
    isLoading,
    fetchedToneOfVoice.data?.data,
    selectedConversation,
    setToneOfVoice,
  ]);

  const handleSelectToneOfVoice = (selectedTone: ToneOfVoice) => {
    setToneOfVoice(selectedTone);
    setPopoverOpen(false);
  };

  const isToneLocked =
    selectedConversation?.id !== undefined &&
    selectedConversation.id !== "temp-id";

  const domainName = getDomainName(getDomain(window.location.hostname));

  const dialogTitle =
    toneDetails.title ||
    chipLabel ||
    (selectedConversation?.id === "temp-id" ? "Valgt tone" : "Tone of voice");

  const openDetails = () => {
    setPopoverOpen(false);
    setTooltipVisible(false);
    setDetailsOpen(true);
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <ToneToolbarVerticalRule />
        <div
          className="flex min-w-0 items-center gap-0"
          role="group"
          aria-label="Tone of voice"
        >
          <Popover
            open={popoverOpen}
            onOpenChange={(open) => {
              if (!disabled) {
                setPopoverOpen(open);
                if (!open) {
                  setTooltipVisible(false);
                }
              }
            }}
          >
            <Tooltip>
              <TooltipTrigger
                asChild
                onPointerEnter={
                  disabled ? undefined : () => setTooltipVisible(true)
                }
                onPointerLeave={
                  disabled ? undefined : () => setTooltipVisible(false)
                }
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "prompt-library-icon flex h-8 min-h-8 max-h-8 max-w-[min(100%,18rem)] shrink items-center gap-1.5 px-2 py-0 font-normal overflow-hidden",
                      chipLabel ? "pl-1.5 pr-1" : "px-2.5"
                    )}
                    disabled={disabled}
                    aria-label="Tone — menu"
                    id="tone-of-voice-menu-trigger"
                  >
                    <ToneOfVoiceIcon
                      className="prompt-library-icon shrink-0"
                      size={20}
                    />
                    {chipLabel ? (
                      <Badge
                        id="locked-tone-of-voice-chip"
                        variant="secondary"
                        className="pointer-events-none inline-flex h-5 min-h-5 max-h-5 min-w-0 max-w-[min(11rem,28vw)] shrink items-center truncate px-2 py-0 font-normal text-xs leading-none text-muted-foreground"
                        title={
                          canOpenDetails
                            ? "Åbn menu for at se beskrivelse og prompt"
                            : lockedPromptExcerpt ||
                              (selectedConversation?.id !== "temp-id"
                                ? chipLabel
                                : undefined)
                        }
                      >
                        <span className="truncate">{chipLabel}</span>
                      </Badge>
                    ) : null}
                    <ChevronDown
                      className="size-4 shrink-0 text-muted-foreground opacity-70"
                      aria-hidden
                    />
                  </Button>
                </PopoverTrigger>
              </TooltipTrigger>
              {tooltipVisible && (
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">
                    {domainName}&apos;s tone of voice — kan kun vælges i starten
                    af en samtale. Åbn menuen for at skifte tone eller se
                    beskrivelse og den fulde instruktion.
                  </p>
                  {lockedPromptExcerpt ? (
                    <p className="mt-2 border-t border-border pt-2 text-xs text-muted-foreground whitespace-pre-wrap">
                      Forhåndsvisning: {lockedPromptExcerpt}
                    </p>
                  ) : null}
                </TooltipContent>
              )}
            </Tooltip>
            <PopoverContent
              align="start"
              className="w-[min(20rem,calc(100vw-2rem))] overflow-hidden p-0"
            >
              <div className="border-b border-border/80 bg-muted/15 px-3 py-2.5">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Tone
                </p>
                {showSummaryCard ? (
                  <div className="mt-2 rounded-md border border-border/70 bg-background/80 px-2.5 py-2 shadow-sm">
                    {summaryTitle ? (
                      <p className="truncate text-sm font-medium text-foreground">
                        {summaryTitle}
                      </p>
                    ) : null}
                    {summaryDescription ? (
                      <p className="mt-1 text-xs leading-snug text-muted-foreground line-clamp-5">
                        {summaryDescription}
                      </p>
                    ) : !summaryTitle ? (
                      <p className="text-xs text-muted-foreground">—</p>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {isToneLocked ? (
                <div className="px-3 pt-3">
                  <div className="flex gap-2 rounded-md border border-border/60 bg-muted/25 px-2.5 py-2 text-xs text-muted-foreground">
                    <Lock
                      className="mt-0.5 size-3.5 shrink-0 text-muted-foreground"
                      aria-hidden
                    />
                    <span>
                      Denne tone er låst for samtalen (som da den startede).
                    </span>
                  </div>
                  <p className="mt-3 text-[11px] font-medium text-muted-foreground">
                    Valgt tone
                  </p>
                  <div
                    className="mt-1.5 flex w-full items-center justify-between gap-2 rounded-md border-l-2 border-primary bg-muted/40 px-2 py-2"
                    aria-current="true"
                  >
                    <span className="truncate text-sm text-foreground">
                      {chipLabel || "—"}
                    </span>
                    <Check
                      className="size-4 shrink-0 text-primary"
                      strokeWidth={2.5}
                      aria-hidden
                    />
                  </div>
                </div>
              ) : (
                <div className="px-3 pt-3">
                  <p className="text-[11px] font-medium text-muted-foreground">
                    Vælg tone
                  </p>
                  <div className="mt-1.5 flex flex-col gap-0.5">
                    {isLoading ? (
                      <div className="flex justify-center py-6">
                        <Spinner />
                      </div>
                    ) : null}
                    {!isLoading &&
                    (fetchedToneOfVoice.data?.data?.length ?? 0) > 0 ? (
                      <div
                        className="flex flex-col gap-0.5"
                        role="radiogroup"
                        aria-label="Vælg tone"
                      >
                        {fetchedToneOfVoice.data?.data.map((tov) => {
                          const selected = currentToneId === tov.id;
                          return (
                            <Button
                              key={tov.id}
                              type="button"
                              variant="ghost"
                              size="sm"
                              role="radio"
                              aria-checked={selected}
                              className={cn(
                                "h-auto w-full justify-between gap-2 rounded-md border-l-2 border-transparent px-2 py-2 font-normal",
                                selected &&
                                  "border-primary bg-muted/45 text-foreground"
                              )}
                              onClick={() => handleSelectToneOfVoice(tov)}
                            >
                              <span className="min-w-0 truncate text-left text-sm">
                                {tov.title}
                              </span>
                              {selected ? (
                                <Check
                                  className="size-4 shrink-0 text-primary"
                                  strokeWidth={2.5}
                                  aria-hidden
                                />
                              ) : (
                                <span className="inline-flex size-4 shrink-0" />
                              )}
                            </Button>
                          );
                        })}
                      </div>
                    ) : null}
                    {!isLoading &&
                    (fetchedToneOfVoice.data?.data?.length ?? 0) === 0 ? (
                      <p className="py-4 text-center text-sm text-muted-foreground">
                        Ingen tone of voice oprettet endnu. Bruger {domainName}{" "}
                        standard tone of voice.
                      </p>
                    ) : null}
                  </div>
                </div>
              )}

              <div className="border-t border-border bg-muted/10 px-3 py-2">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className={cn(
                    "h-auto w-full justify-start gap-2 px-0 py-1.5 text-sm font-normal text-muted-foreground no-underline hover:text-foreground hover:no-underline",
                    !canOpenDetails && "pointer-events-none opacity-50"
                  )}
                  disabled={!canOpenDetails}
                  onClick={openDetails}
                  id="tone-of-voice-view-details-button"
                >
                  <Info className="size-4 shrink-0" aria-hidden />
                  Se beskrivelse og prompt
                </Button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <ToneToolbarVerticalRule />
      </div>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent size="xl" id="tone-of-voice-details-dialog">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <section>
              <h3 className="text-sm font-medium text-foreground mb-1.5">
                Beskrivelse
              </h3>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {toneDetails.description.trim() ? toneDetails.description : "—"}
              </p>
            </section>
            <section>
              <h3 className="text-sm font-medium text-foreground mb-1.5">
                Instruktion (prompt)
              </h3>
              <pre className="text-sm whitespace-pre-wrap font-sans text-muted-foreground border border-border rounded-md p-3 bg-muted/50 max-h-[min(65vh,40rem)] overflow-y-auto">
                {toneDetails.prompt.trim() ? toneDetails.prompt : "—"}
              </pre>
            </section>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="default"
              onClick={() => setDetailsOpen(false)}
              id="tone-of-voice-details-close"
            >
              Luk
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export interface CustomIconProps {
  className?: string;
  size?: number;
}

export const ToneOfVoiceIcon: React.FC<CustomIconProps> = ({
  className,
  size = 16,
}) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    style={{ width: size, height: size }}
  >
    <path
      d="M19.71 24C19.37 24 19.0852 23.8813 18.8556 23.644C18.626 23.4067 18.5108 23.1134 18.51 22.7641C18.51 22.5993 18.54 22.4345 18.6 22.2697C18.66 22.1049 18.74 21.971 18.84 21.868C19.7 20.9823 20.3752 19.9627 20.8656 18.8092C21.356 17.6556 21.6008 16.3991 21.6 15.0396C21.6 13.7007 21.3552 12.4496 20.8656 11.2861C20.376 10.1227 19.7008 9.09776 18.84 8.21119C18.74 8.1082 18.66 7.97431 18.6 7.80952C18.54 7.64473 18.51 7.47995 18.51 7.31516C18.51 6.96498 18.6252 6.67166 18.8556 6.43519C19.086 6.19872 19.3708 6.08007 19.71 6.07924C19.87 6.07924 20.0252 6.1155 20.1756 6.188C20.326 6.26051 20.4508 6.34785 20.55 6.45002C21.63 7.56234 22.4752 8.85511 23.0856 10.3283C23.696 11.8015 24.0008 13.372 24 15.0396C24 16.7287 23.6948 18.3045 23.0844 19.767C22.474 21.2295 21.6296 22.5169 20.5512 23.6292C20.4512 23.7322 20.3264 23.82 20.1768 23.8925C20.0272 23.965 19.872 24.0008 19.7112 24M16.2912 20.4776C15.9512 20.4776 15.6664 20.3594 15.4368 20.1229C15.2072 19.8865 15.092 19.5927 15.0912 19.2417C15.0912 19.0769 15.1264 18.9175 15.1968 18.7634C15.2672 18.6093 15.352 18.4804 15.4512 18.3766C15.8712 17.944 16.2012 17.4443 16.4412 16.8774C16.6812 16.3105 16.8012 15.6979 16.8012 15.0396C16.8012 14.3813 16.6764 13.7687 16.4268 13.2018C16.1772 12.6349 15.852 12.1352 15.4512 11.7027C15.3312 11.5997 15.2412 11.4711 15.1812 11.317C15.1212 11.163 15.0912 11.0031 15.0912 10.8375C15.0912 10.4873 15.2064 10.194 15.4368 9.95754C15.6672 9.72107 15.952 9.60242 16.2912 9.6016C16.4512 9.6016 16.6112 9.63249 16.7712 9.69429C16.9312 9.75609 17.0612 9.84878 17.1612 9.97237C17.8012 10.6315 18.3012 11.3937 18.6612 12.2588C19.0212 13.124 19.2012 14.0509 19.2012 15.0396C19.2012 16.0283 19.0212 16.9553 18.6612 17.8204C18.3012 18.6856 17.8012 19.4477 17.1612 20.1069C17.0612 20.2305 16.9312 20.3231 16.7712 20.3849C16.6112 20.4467 16.4512 20.4776 16.2912 20.4776ZM7.2 14.9778H4.8V15.3177C4.8 16.0386 5.0152 16.6723 5.4456 17.2185C5.876 17.7648 6.4208 18.1302 7.08 18.3148L7.44 18.4075C8.24 18.6135 8.69 19.1284 8.79 19.9524C8.89 20.7763 8.58 21.3943 7.86 21.8062C6.84 22.4036 5.77 22.8362 4.65 23.104C3.53 23.3717 2.38 23.5365 1.2 23.5983C0.860004 23.6189 0.575204 23.5106 0.345604 23.2733C0.116004 23.036 0.000804138 22.7427 4.13793e-06 22.3933C-0.000795862 22.0439 0.114404 21.7506 0.345604 21.5133C0.576804 21.276 0.861604 21.1471 1.2 21.1265C1.92 21.0853 2.6252 20.9975 3.3156 20.8632C4.006 20.7289 4.6908 20.5386 5.37 20.2922C4.45 19.8185 3.7252 19.1338 3.1956 18.2382C2.666 17.3425 2.4008 16.369 2.4 15.3177V13.7419C2.4 13.3917 2.5152 13.0984 2.7456 12.8619C2.976 12.6255 3.2608 12.5068 3.6 12.506H7.2V10.0342C7.2 9.68399 7.3152 9.39067 7.5456 9.1542C7.776 8.91772 8.0608 8.79908 8.4 8.79825H11.25L7.86 1.81534C7.7 1.50636 7.6752 1.19244 7.7856 0.873576C7.896 0.554711 8.1008 0.312472 8.4 0.146859C8.6992 -0.0187531 9.0044 -0.0442954 9.3156 0.0702326C9.6268 0.184761 9.8616 0.39569 10.02 0.70302L13.41 7.68593C13.81 8.50987 13.78 9.31322 13.32 10.096C12.86 10.8787 12.18 11.2701 11.28 11.2701H9.6V12.506C9.6 13.1857 9.3652 13.7679 8.8956 14.2523C8.426 14.7368 7.8608 14.9786 7.2 14.9778Z"
      fill="currentColor"
    />
  </svg>
);

export default ToneOfVoicePopover;
